import { localStorage } from 'lib/storage';
import mapValues from 'lodash/mapValues';
import sumBy from 'lodash/sumBy';
import {
	atom,
	AtomEffect,
	AtomOptions,
	DefaultValue,
	ReadOnlySelectorOptions,
	ReadWriteSelectorOptions,
	RecoilState,
	RecoilValueReadOnly,
	selector,
} from 'recoil';
import { ProductInCart } from 'types/cart/productInCart';
import { CheckoutStore, ICheckoutKeys } from 'types/checkout';
import { calculateTotalPriceWithTax } from '../lib/price';
import { DEFAULT_RESOURCE_QTY } from 'components/Checkout/program/step/types/checkout.consts';

const CHECKOUT_STORAGE_KEY = 'checkoutState';

/**
 * Create an effect that syncs with local storage.
 *
 * @param key - the key to use in local storage
 * @returns the atom effect that syncs with local storage
 */
export function localStorageEffect<T>(key: string): AtomEffect<T> {
	return function ({ setSelf, onSet }) {
		// Get the previously saved value from local storage
		const savedValue = localStorage.getItem(key);

		// If a saved value exists, use it for our "self"
		if (savedValue !== null) {
			setSelf(savedValue);
		}

		// Whenever our "self" is updated, sync with local storage
		onSet(newValue => {
			if (newValue instanceof DefaultValue) {
				// If the new value is a DefaultValue, remove from local storage
				localStorage.removeItem(key);
			} else {
				// Otherwise, update the value in local storage
				localStorage.setItem(key, newValue);
			}
		});
	};
}

/**
 * Default options for checkout atoms
 *
 * @returns default atom options for checkout
 */
function defaultCheckoutOptions<T>(): AtomOptions<T> {
	return {
		key: '',
		default: <T>{},
		// Based on:
		// https://recoiljs.org/docs/guides/atom-effects/#local-storage-persistence
		effects_UNSTABLE: [localStorageEffect<T>(CHECKOUT_STORAGE_KEY)],
	};
}

/**
 * Factory function to create a checkout atom
 *
 * @param options - customizable atom options
 * @returns checkout atom created with the provided options
 */
export function CheckoutFactory<T>(options: Partial<AtomOptions<T>> = defaultCheckoutOptions<T>()) {
	const defaultOptions = defaultCheckoutOptions<T>();

	// Use provided options where they exist, otherwise use defaults
	const config = Object.assign(defaultOptions, options);

	// Create and return the checkout atom
	return atom<T>(config as AtomOptions<T>);
}

/**
 * Factory function to create checkout cart booking fee selectors
 *
 * @param options - customizable selector options
 * @returns checkout cart booking fee selector
 */
export function CheckoutCartBookingFeeFactory(options: Partial<ReadWriteSelectorOptions<number>>) {
	const defaultOptions = { key: '', get: () => 0 };

	// Use provided options where they exist, otherwise use defaults
	const config = Object.assign(defaultOptions, options);

	// Create and return the selector
	return selector<number>(config);
}

/**
 * Factory function to create checkout cart total price selectors, used for calculating total price of items
 *
 * @param options - customizable selector options
 * @param checkoutCartItems - array of items in the cart, used for price calculation
 * @returns checkout cart total price selector
 */
export function CheckoutCartTotalPriceFactory(
	options: Partial<ReadWriteSelectorOptions<number>>,
	checkoutCartItems: RecoilValueReadOnly<ProductInCart[]>
) {
	const defaultOptions: ReadOnlySelectorOptions<number> = {
		key: '',
		get: ({ get }) => {
			// Accumulate the total price of items in the cart
			const checkoutCartItemsState = get(checkoutCartItems);
			return sumBy(checkoutCartItemsState, ({ product, quantity }) => {
				return calculateTotalPriceWithTax((product?.currPrice?.price ?? 0) * (quantity || DEFAULT_RESOURCE_QTY), product?.isTaxInclusive, product?.tax);
			});
		},
	};

	// Use provided options where they exist, otherwise use defaults
	const config = Object.assign(defaultOptions, options);

	// Create and return the selector
	return selector<number>(config);
}

/**
 * Factory function to create checkout event applications selectors
 *
 * @param options - customizable selector options
 * @param checkout - checkout state atom, used for obtaining selected answers
 * @returns checkout event applications selector
 */
export function CheckoutEventApplicationsFactory<T>(
	options: Partial<ReadWriteSelectorOptions<unknown>>,
	checkout: RecoilState<CheckoutStore & T>
) {
	const defaultOptions: ReadOnlySelectorOptions<unknown> = {
		key: '',
		get: ({ get }) => {
			// Obtain the selected answers from the checkout state
			const checkoutState = get(checkout);

			return checkoutState?.selectedAnswersForInit ?? {};
		},
	};

	// Use provided options where they exist, otherwise use defaults
	const config = Object.assign(defaultOptions, options);

	// Create and return the selector
	return selector(config);
}

/**
 * Generates checkout keys given a specific area.
 *
 * @param area - the specific area of checkout
 * @returns an object containing checkout keys
 */
export function checkoutKeyGen(area: string): ICheckoutKeys {
	let result: ICheckoutKeys = {
		Checkout: 'Checkout', // e.g area: league -> 'leagueCheckout'
		CheckoutCartItems: 'CheckoutCartItems',
		CheckoutCartBookingFee: 'CheckoutCartBookingFee',
		CheckoutCartTotalPrice: 'CheckoutCartTotalPrice',
		CheckoutEventApplications: 'CheckoutEventApplications',
	};

	// append area to each of the keys
	result = mapValues(result, value => area + value);

	return result;
}
